import { useEffect, useRef } from 'react'
import type { GeoJSONSource, MapMouseEvent } from 'maplibre-gl'
import { useContactStore, selectFilteredContacts } from '../../store/contactStore'
import { useMapContacts } from '../../hooks/useContacts'
import { useMapInstance } from '../../hooks/useMap'
import {
  CIRCLE_BLUR,
  CIRCLE_COLOR,
  CIRCLE_OPACITY,
  CIRCLE_RADIUS,
  CIRCLE_STROKE_OPACITY,
  CIRCLE_STROKE_WIDTH,
} from './confidenceStyle'
import type { ContactSummary } from '../../types/contact'

const SOURCE_ID = 'bio-contacts'
const CIRCLE_LAYER = 'bio-contact-circles'
const CLUSTER_LAYER = 'bio-contact-clusters'
const CLUSTER_COUNT_LAYER = 'bio-contact-cluster-count'

// Nearby unconfirmed reports are exactly the case clustering most needs to declutter — a single
// incident can generate a dozen overlapping low-confidence pins before it's corroborated.
const CLUSTER_RADIUS_PX = 50
const CLUSTER_MAX_ZOOM = 14

// Neutral slate ramp (distinct from the confidence-status palette) so a cluster bubble always
// reads as "N contacts here", not as a status of its own — the leaf circles keep the confidence
// coding once a cluster splits apart.
const CLUSTER_CIRCLE_COLOR = [
  'step', ['get', 'point_count'],
  '#64748b', 10,
  '#475569', 50,
  '#1e293b',
]
const CLUSTER_CIRCLE_RADIUS = [
  'step', ['get', 'point_count'],
  16, 10,
  20, 50,
  26,
]

function buildGeoJSON(contacts: ContactSummary[]) {
  return {
    type: 'FeatureCollection' as const,
    features: contacts.map((c) => ({
      type: 'Feature' as const,
      id: c.id,
      geometry: { type: 'Point' as const, coordinates: [c.lon, c.lat] },
      properties: { id: c.id, status: c.status, species_bucket: c.species_bucket },
    })),
  }
}

export default function ContactLayer() {
  const { mapRef, mapReady } = useMapInstance()
  const selectContact = useContactStore((s) => s.selectContact)
  const filters = useContactStore((s) => s.filters)
  const contacts = useContactStore(selectFilteredContacts)

  useMapContacts(filters)

  const contactsRef = useRef(contacts)
  contactsRef.current = contacts

  useEffect(() => {
    const map = mapRef.current
    if (!map) return

    let cancelled = false

    const handleClick = (e: MapMouseEvent & { features?: { properties?: { id?: string } }[] }) => {
      const id = e.features?.[0]?.properties?.id
      if (id) selectContact(id)
    }
    const handleMouseEnter = () => { map.getCanvas().style.cursor = 'pointer' }
    const handleMouseLeave = () => { map.getCanvas().style.cursor = '' }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const handleClusterClick = (e: MapMouseEvent & { features?: any[] }) => {
      const feature = e.features?.[0]
      const clusterId = feature?.properties?.cluster_id
      if (clusterId == null) return
      const src = map.getSource(SOURCE_ID) as GeoJSONSource | undefined
      src?.getClusterExpansionZoom(clusterId).then((zoom) => {
        const [lon, lat] = feature.geometry.coordinates as [number, number]
        map.easeTo({ center: [lon, lat], zoom, duration: 500 })
      }).catch(() => {})
    }

    const addLayerAndData = () => {
      if (!map.getSource(SOURCE_ID)) {
        map.addSource(SOURCE_ID, {
          type: 'geojson',
          data: { type: 'FeatureCollection', features: [] },
          cluster: true,
          clusterRadius: CLUSTER_RADIUS_PX,
          clusterMaxZoom: CLUSTER_MAX_ZOOM,
        })
        map.addLayer({
          id: CLUSTER_LAYER,
          type: 'circle',
          source: SOURCE_ID,
          filter: ['has', 'point_count'],
          paint: {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            'circle-radius': CLUSTER_CIRCLE_RADIUS as any,
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            'circle-color': CLUSTER_CIRCLE_COLOR as any,
            'circle-opacity': 0.85,
            'circle-stroke-color': '#ffffff',
            'circle-stroke-width': 2,
            'circle-stroke-opacity': 0.9,
          },
        })
        map.addLayer({
          id: CLUSTER_COUNT_LAYER,
          type: 'symbol',
          source: SOURCE_ID,
          filter: ['has', 'point_count'],
          layout: {
            'text-field': ['get', 'point_count_abbreviated'],
            'text-size': 12,
            'text-font': ['Noto Sans Bold', 'Open Sans Bold', 'Arial Unicode MS Bold'],
          },
          paint: { 'text-color': '#ffffff' },
        })
        map.addLayer({
          id: CIRCLE_LAYER,
          type: 'circle',
          source: SOURCE_ID,
          filter: ['!', ['has', 'point_count']],
          paint: {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            'circle-radius': CIRCLE_RADIUS as any,
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            'circle-color': CIRCLE_COLOR as any,
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            'circle-opacity': CIRCLE_OPACITY as any,
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            'circle-blur': CIRCLE_BLUR as any,
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            'circle-stroke-color': CIRCLE_COLOR as any,
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            'circle-stroke-width': CIRCLE_STROKE_WIDTH as any,
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            'circle-stroke-opacity': CIRCLE_STROKE_OPACITY as any,
          },
        })
      }
      const src = map.getSource(SOURCE_ID) as GeoJSONSource | undefined
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      src?.setData(buildGeoJSON(contactsRef.current) as any)

      map.on('click', CIRCLE_LAYER, handleClick)
      map.on('mouseenter', CIRCLE_LAYER, handleMouseEnter)
      map.on('mouseleave', CIRCLE_LAYER, handleMouseLeave)
      map.on('click', CLUSTER_LAYER, handleClusterClick)
      map.on('mouseenter', CLUSTER_LAYER, handleMouseEnter)
      map.on('mouseleave', CLUSTER_LAYER, handleMouseLeave)
    }

    const setup = () => {
      if (cancelled) return
      addLayerAndData()
    }

    const onStyleLoad = () => {
      if (cancelled) return
      addLayerAndData()
    }
    map.on('style.load', onStyleLoad)

    if (map.isStyleLoaded()) setup()
    else map.once('load', setup)

    return () => {
      cancelled = true
      map.off('style.load', onStyleLoad)
      try {
        map.off('click', CIRCLE_LAYER, handleClick)
        map.off('mouseenter', CIRCLE_LAYER, handleMouseEnter)
        map.off('mouseleave', CIRCLE_LAYER, handleMouseLeave)
        map.off('click', CLUSTER_LAYER, handleClusterClick)
        map.off('mouseenter', CLUSTER_LAYER, handleMouseEnter)
        map.off('mouseleave', CLUSTER_LAYER, handleMouseLeave)
      } catch {
        // map may already be removed
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mapReady])

  useEffect(() => {
    const map = mapRef.current
    if (!map) return
    const source = map.getSource(SOURCE_ID) as GeoJSONSource | undefined
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    source?.setData(buildGeoJSON(contacts) as any)
  }, [contacts, mapRef])

  return null
}
